import { Suspense, lazy, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/navigation/AppSidebar";
import { AppNavigation } from "@/components/navigation/AppNavigation";
import { TrialBanner } from "@/components/trial/TrialBanner";
import { TrialExpired } from "@/pages/TrialExpired";
import { useLogout } from "@/hooks/useLogout";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { useIsMobile } from "@/hooks/use-mobile";
import { SIDEBAR } from "@/lib/uiConstants";
import { ContentSkeleton } from "@/components/layout/ContentSkeleton";
import { ModalLoadingFallback } from "@/components/ui/ModalLoadingFallback";
import { ErrorBoundary } from "../ErrorBoundary";
import { preloadMainRoutes } from "@/lib/routePreload";
import { OrdersRealtimeProvider } from "@/providers/OrdersRealtimeProvider";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOnboarding } from "@/hooks/useOnboarding";
import { WifiOff } from "lucide-react";

const OnboardingModal = lazy(() => import("@/components/onboarding/OnboardingModal").then(m => ({ default: m.OnboardingModal })));

/**
 * AppShell - Persistent Application Shell
 * 
 * Uses a 2-column grid layout where:
 * - Header spans full width with purple gradient
 * - Header left column aligns with sidebar width (contains toggle + logo)
 * - Header right column contains nav controls
 * - Below header: sidebar + content in same grid structure
 * 
 * CRITICAL: This component must be imported eagerly (not lazy-loaded)
 * to prevent remounts when navigating between app routes.
 */
export function AppShell() {
  return (
    <ErrorBoundary>
    <SidebarProvider>
      <AppShellContent />
    </SidebarProvider>
    </ErrorBoundary>
  );
}

function AppShellContent() {
  const { handleLogout } = useLogout();
  const { status, daysRemaining, isLoading } = useTrialStatus();
  const isMobile = useIsMobile();
  const { open: isExpanded } = useSidebar();
  const isOnline = useNetworkStatus();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { showOnboarding, setShowOnboarding, markOnboardingComplete } = useOnboarding();

  const handleOnboardingComplete = () => {
    markOnboardingComplete();
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    markOnboardingComplete();
    setShowOnboarding(false);
  };

  // Compute sidebar width - single source of truth
  const sidebarWidth = isExpanded ? SIDEBAR.expanded : SIDEBAR.collapsed;

  // Check if trial has ended - but keep shell mounted!
  const isTrialExpired = !isLoading && (
    status === "expired" || 
    (status === "trial" && daysRemaining !== null && daysRemaining <= 0)
  );

  // Preload main routes during browser idle time
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => preloadMainRoutes());
      return () => cancelIdleCallback(id);
    } else {
      // Fallback for Safari: preload after 2 seconds
      const timer = setTimeout(preloadMainRoutes, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <OrdersRealtimeProvider>
      <div className="min-h-screen flex flex-col w-full overflow-x-hidden">
        {/* Fixed header - full width purple, 2-column grid layout */}
        <AppNavigation 
          onLogout={handleLogout} 
          sidebarWidth={sidebarWidth}
          className="fixed top-0 left-0 right-0 z-30" 
        />
        
        {/* Main content area - below header */}
        <div 
          className="flex-1 pt-14"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : `${sidebarWidth}px 1fr`,
            transition: `grid-template-columns ${SIDEBAR.animationMs}ms ease`,
          }}
        >
          {/* Sidebar - renders Sheet on mobile, persistent sidebar on desktop */}
          <AppSidebar />
          
          {/* Content area with subtle divider on left */}
          <main className="min-w-0 overflow-x-hidden flex flex-col border-l border-[hsl(var(--sidebar-divider))]">
            {/* Offline banner - network awareness */}
            {!isOnline && (
              <div className="bg-warning/15 border-b border-warning/30 px-4 py-2 flex items-center gap-2 text-sm text-warning-foreground">
                <WifiOff className="h-4 w-4 text-warning flex-shrink-0" />
                <span>{t("common.offlineBanner")}</span>
              </div>
            )}
            
            {/* Subtle trial banner - ambient presence */}
            <TrialBanner />
            
            {/* Content: either TrialExpired or page content */}
            {isTrialExpired ? (
              <TrialExpired />
            ) : (
              // Suspense boundary here - only content swaps, shell stays mounted
              <Suspense fallback={<ContentSkeleton />}>
                <Outlet />
              </Suspense>
            )}
          </main>
        </div>
      </div>
      {/* Onboarding Modal - global, available on all pages */}
      {showOnboarding && (
        <Suspense fallback={<ModalLoadingFallback />}>
          <OnboardingModal
            open={showOnboarding}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
            onCreateManual={() => { handleOnboardingComplete(); navigate('/orders/new'); }}
            onCreateSmart={() => { handleOnboardingComplete(); navigate('/orders/new?tab=smart'); }}
            onBulkUpload={() => { handleOnboardingComplete(); navigate('/orders/bulk-upload'); }}
          />
        </Suspense>
      )}
    </OrdersRealtimeProvider>
  );
}
